import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Star } from 'lucide-react';
import { vendorService } from '../services/api';

import SearchAutocomplete from '../components/SearchAutocomplete';

const Home = () => {
  const navigate = useNavigate();

  // Filter states
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Dropdown list states
  const [states, setStates] = useState([]);
  const [citiesMap, setCitiesMap] = useState({});
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);

  // Load locations and categories on mount
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const locRes = await vendorService.getPublicLocations();
        if (locRes.success && locRes.data) {
          setStates(locRes.data.states || []);
          setCitiesMap(locRes.data.cities || {});
        }
        
        // Scope Home page search bar category dropdown exclusively to VENDOR categories
        const catRes = await vendorService.getCategories(false, 'ALL', 'VENDOR');
        if (catRes.success && catRes.data) {
          setCategories(catRes.data || []);
        }

        // Load all categories to resolve footer popular categories & tags to their correct section
        const allCatRes = await vendorService.getCategories(false, 'ALL');
        if (allCatRes.success && allCatRes.data) {
          setAllCategories(allCatRes.data || []);
        }
      } catch (err) {
        console.error("Error loading home page search metadata:", err);
      }
    };
    loadMetadata();
  }, []);

  const handleStateSelect = (val) => {
    setSelectedState(val);
    setSelectedCity('');
  };

  const handleCitySelect = (val) => {
    setSelectedCity(val);
    if (val) {
      for (const state in citiesMap) {
        if (citiesMap[state].includes(val)) {
          setSelectedState(state);
          break;
        }
      }
    }
  };

  const handleCategorySelect = (val) => {
    setSelectedCategory(val);
  };

  const handleSearchSubmit = () => {
    const params = new URLSearchParams();
    if (selectedState) params.append('state', selectedState);
    if (selectedCity) params.append('city', selectedCity);
    if (selectedCategory) params.append('categoryId', selectedCategory);
    
    navigate(`/listing?${params.toString()}`);
  };

  const handleCategoryClick = (categoryName) => {
    const listToSearch = allCategories.length > 0 ? allCategories : categories;
    const found = listToSearch.find(c => c.name.toLowerCase() === categoryName.toLowerCase());
    if (found) {
      const isDesigner = (found.section || 'VENDOR').toUpperCase() === 'DESIGNER_ARCHITECT';
      const targetPath = isDesigner ? '/interior-designers-and-architects' : '/listing';
      navigate(`${targetPath}?categoryId=${found.id}`);
    } else {
      navigate(`/listing?searchName=${encodeURIComponent(categoryName)}`);
    }
  };

  const handleLocationClick = (cityName) => {
    let stateName = '';
    for (const state in citiesMap) {
      if (citiesMap[state].includes(cityName)) {
        stateName = state;
        break;
      }
    }
    const params = new URLSearchParams();
    if (stateName) params.append('state', stateName);
    params.append('city', cityName);
    navigate(`/listing?${params.toString()}`);
  };

  const handleTagClick = (tagName) => {
    let foundTag = null;
    let foundCat = null;
    const listToSearch = allCategories.length > 0 ? allCategories : categories;
    for (const cat of listToSearch) {
      if (cat.tags) {
        const t = cat.tags.find(tag => {
          const nameA = tag.name.toLowerCase();
          const nameB = tagName.toLowerCase();
          return nameA === nameB || nameA.startsWith(nameB) || nameB.startsWith(nameA);
        });
        if (t) {
          foundTag = t;
          foundCat = cat;
          break;
        }
      }
    }
    if (foundTag && foundCat) {
      const isDesigner = (foundCat.section || 'VENDOR').toUpperCase() === 'DESIGNER_ARCHITECT';
      const targetPath = isDesigner ? '/interior-designers-and-architects' : '/listing';
      navigate(`${targetPath}?categoryId=${foundCat.id}&tagId=${foundTag.id}`);
    } else {
      navigate(`/listing?searchName=${encodeURIComponent(tagName)}`);
    }
  };

  const availableCities = selectedState 
    ? (citiesMap[selectedState] || []) 
    : Array.from(new Set(Object.values(citiesMap).flat()));

  const categoriesRow1 = [
    { name: 'Civil Engineer', icon: '/Categories/1f3d7.svg' },
    { name: 'Carpenter', icon: '/Categories/1fa9a.svg' },
    { name: 'Electrician', icon: '/Categories/26a1.svg' },
    { name: 'Waterproofing Contractor', icon: '/Categories/1f4a7.svg' },
    { name: 'Plumbing & Sanitary', icon: '/Categories/1f6bf.svg' },
    { name: 'False Ceiling & Partitions', icon: '/Categories/1f528.svg' },
    { name: 'Landscape & Outdoor', icon: '/Categories/1f333.svg' },
  ];

  const categoriesRow2 = [
    { name: 'Modular Kitchen & Wardrobe', icon: '/Categories/1f37d.svg' },
    { name: 'Furniture', icon: '/Categories/1f6cb.svg' },
    { name: 'Lighting', icon: '/Categories/1f4a1.svg' },
    { name: 'Home Automation', icon: '/Categories/1f916.svg' },
    { name: 'Movers & Installers', icon: '/Categories/1f69a.svg' },
    { name: '3D & Visualization', icon: '/Categories/1f9ca.svg' },
    { name: 'Vastu Consultant', icon: '/Categories/1f9ed.svg' },
    { name: 'Swings Manufacturer', icon: '/Categories/1fa91.svg' },
  ];

  const locations = [
    {
      name: 'Ahmedabad',
      image: '/locations/Ahmedabad.jpg'
    },
    {
      name: 'Mumbai',
      image: '/locations/Mumbai.png'
    },
    {
      name: 'Bangalore',
      image: '/locations/Bangalore.png'
    },
    {
      name: 'Delhi',
      image: '/locations/Delhi.jpg'
    },
  ];

  const testimonials = [
    { 
      name: 'Rajesh Patel', 
      username: '@rajesh_patel', 
      text: 'Top Interiors India has completely transformed how we source custom furniture and fixtures. The vendors are highly professional and verified.' 
    },
    { 
      name: 'Anjali Sharma', 
      username: '@anjali_sharma', 
      text: 'It is incredibly convenient to connect with designers and material suppliers. Sourced all false ceiling materials in just one click!' 
    },
    { 
      name: 'Vikram Malhotra', 
      username: '@vikram_malhotra', 
      text: 'Outstanding platform for local vendors! The visibility our modular wardrobe listing received here has doubled our customer inquiries.' 
    },
  ];

  return (
    <div className="overflow-hidden font-sans bg-white">
      {/* Hero Section */}
      <section className="relative h-[50vh] sm:h-[80vh] flex items-center justify-center pt-24 bg-secondary">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&q=80&w=1920"
            alt="Interior"
            className="w-full h-full object-cover opacity-60"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-30 text-center w-full">
          <h1 className="text-4xl md:text-[44px] font-medium text-white mb-6 tracking-tight">
            Find Verified Vendor
          </h1>

          {/* Desktop Search Section */}
          <div className="hidden md:flex bg-white rounded-xl shadow-lg p-1.5 flex-row items-center max-w-[780px] mx-auto w-full">
            <div className="flex-1 w-full border-r border-gray-200 relative flex items-center">
              <SearchAutocomplete
                placeholder="State"
                value={selectedState}
                options={states}
                onSelect={(val) => handleStateSelect(val)}
                inputClassName="w-full bg-transparent pl-6 pr-10 py-3 text-[14px] text-gray-800 focus:outline-none cursor-pointer font-medium placeholder-gray-500"
              />
            </div>

            <div className="flex-1 w-full border-r border-gray-200 relative flex items-center">
              <SearchAutocomplete
                placeholder="City"
                value={selectedCity}
                options={availableCities}
                onSelect={(val) => handleCitySelect(val)}
                inputClassName="w-full bg-transparent pl-6 pr-10 py-3 text-[14px] text-gray-800 focus:outline-none cursor-pointer font-medium placeholder-gray-500"
              />
            </div>

            <div className="flex-1 w-full relative flex items-center pr-4">
              <SearchAutocomplete
                placeholder="Category"
                value={selectedCategory}
                options={categories}
                onSelect={(val) => handleCategorySelect(val)}
                inputClassName="w-full bg-transparent pl-6 pr-10 py-3 text-[14px] text-gray-800 focus:outline-none cursor-pointer font-medium placeholder-gray-500"
              />
            </div>

            <button 
              onClick={handleSearchSubmit}
              className="bg-primary text-white rounded-lg hover:bg-opacity-90 transition-all flex items-center justify-center w-[75px] h-[48px] flex-shrink-0 cursor-pointer"
            >
              <Search size={20} strokeWidth={2.5} />
            </button>
          </div>

          {/* Mobile Search Section */}
          <div className="flex md:hidden bg-white rounded-xl shadow-md p-1 items-center w-full border border-gray-150 gap-1">
            <div className="flex-1 min-w-0 relative flex items-center border-r border-gray-150">
              <SearchAutocomplete
                placeholder="State"
                value={selectedState}
                options={states}
                onSelect={(val) => handleStateSelect(val)}
                dropdownClassName="left-0 right-auto min-w-[190px] sm:min-w-full shadow-xl rounded-xl border border-gray-150"
                inputClassName="w-full bg-transparent pl-2 pr-6 py-2 text-[11px] text-gray-800 focus:outline-none cursor-pointer font-medium placeholder-gray-500"
              />
            </div>

            <div className="flex-1 min-w-0 relative flex items-center border-r border-gray-150">
              <SearchAutocomplete
                placeholder="City"
                value={selectedCity}
                options={availableCities}
                onSelect={(val) => handleCitySelect(val)}
                dropdownClassName="left-0 right-auto min-w-[190px] sm:min-w-full shadow-xl rounded-xl border border-gray-150"
                inputClassName="w-full bg-transparent pl-2 pr-6 py-2 text-[11px] text-gray-800 focus:outline-none cursor-pointer font-medium placeholder-gray-500"
              />
            </div>

            <div className="flex-[1.2] min-w-0 relative flex items-center pr-1">
              <SearchAutocomplete
                placeholder="Category"
                value={selectedCategory}
                options={categories}
                onSelect={(val) => handleCategorySelect(val)}
                dropdownClassName="right-0 left-auto min-w-[210px] sm:min-w-full shadow-xl rounded-xl border border-gray-150"
                inputClassName="w-full bg-transparent pl-2 pr-6 py-2 text-[11px] text-gray-800 focus:outline-none cursor-pointer font-medium placeholder-gray-500"
              />
            </div>

            <button 
              onClick={handleSearchSubmit}
              className="bg-primary text-white rounded-lg hover:bg-opacity-90 transition-all flex items-center justify-center w-9 h-9 flex-shrink-0 cursor-pointer"
            >
              <Search size={14} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </section>

      {/* Location Section - Reduced Margins */}
      <section className="py-6 sm:py-14 lg:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 text-left">
        <div className="mb-5 sm:mb-10">
          <p className="text-[#2ab391] text-[13px] sm:text-[14px] mb-1">Location</p>
          <h2 className="text-2xl sm:text-[32px] md:text-[38px] font-medium text-secondary">Explore Popular Location</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
          {locations.map((loc, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.2 }}
              onClick={() => handleLocationClick(loc.name)}
              className="relative aspect-[3/4] sm:aspect-auto sm:h-[260px] md:h-[380px] rounded-[15px] sm:rounded-[20px] overflow-hidden shadow-md cursor-pointer group border border-gray-100"
            >
              <img
                src={loc.image}
                alt={loc.name}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-5 text-center">
                <h4 className="text-xs sm:text-[16px] font-medium text-white">{loc.name}</h4>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Categories Section - Reduced Margins */}
      <section className="py-6 sm:py-14 lg:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 text-left">
        <div className="mb-5 sm:mb-10">
          <p className="text-[#2ab391] text-[13px] sm:text-[14px] mb-1">Categories</p>
          <h2 className="text-2xl sm:text-[32px] md:text-[38px] font-medium text-secondary">Construction & Execution</h2>
        </div>

        <div className="flex space-x-6 overflow-hidden mb-8 -mx-4 sm:-mx-6 lg:-mx-12">
          <motion.div
            animate={{ x: [0, -1000] }}
            transition={{ repeat: Infinity, duration: 35, ease: "linear" }}
            className="flex space-x-6 whitespace-nowrap px-4 sm:px-6 lg:px-12"
          >
            {[...categoriesRow1, ...categoriesRow1].map((cat, i) => (
              <div 
                key={i} 
                className="min-w-[95px] sm:min-w-[190px] bg-white border border-gray-100 shadow-sm rounded-xl sm:rounded-2xl p-2.5 sm:p-6 flex flex-col items-center justify-center space-y-1.5 sm:space-y-4 transition-all"
              >
                <div className="bg-[#f2fafa] p-2 sm:p-4 rounded-xl">
                  <img src={cat.icon} alt={cat.name} className="w-5 h-5 sm:w-8 sm:h-8 object-contain" />
                </div>
                <p className="font-bold text-secondary text-[9px] sm:text-[12px] text-center whitespace-normal px-1 sm:px-2 leading-tight">{cat.name}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="flex space-x-6 overflow-hidden -mx-4 sm:-mx-6 lg:-mx-12">
          <motion.div
            animate={{ x: [-1000, 0] }}
            transition={{ repeat: Infinity, duration: 35, ease: "linear" }}
            className="flex space-x-6 whitespace-nowrap px-4 sm:px-6 lg:px-12"
          >
            {[...categoriesRow2, ...categoriesRow2].map((cat, i) => (
              <div 
                key={i} 
                className="min-w-[95px] sm:min-w-[190px] bg-white border border-gray-100 shadow-sm rounded-xl sm:rounded-2xl p-2.5 sm:p-6 flex flex-col items-center justify-center space-y-1.5 sm:space-y-4 transition-all"
              >
                <div className="bg-[#f2fafa] p-2 sm:p-4 rounded-xl">
                  <img src={cat.icon} alt={cat.name} className="w-5 h-5 sm:w-8 sm:h-8 object-contain" />
                </div>
                <p className="font-bold text-secondary text-[9px] sm:text-[12px] text-center whitespace-normal px-1 sm:px-2 leading-tight">{cat.name}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section - Reduced Margins */}
      <section className="py-6 sm:py-14 lg:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="mb-5 sm:mb-10">
          <p className="text-[#2ab391] text-[13px] sm:text-[14px] mb-1">Feedback</p>
          <h2 className="text-2xl sm:text-[32px] md:text-[38px] font-medium text-secondary">Testimonials</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-[#f7fdfd] rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="p-5 sm:p-8 pb-3 sm:pb-4">
                <h4 className="font-bold text-[16px] sm:text-lg text-secondary leading-tight">{t.name}</h4>
                <div className="flex text-yellow-400 space-x-1 my-1">
                  {[...Array(5)].map((_, idx) => <Star key={idx} className="w-3.5 h-3.5" fill="currentColor" />)}
                </div>
                <p className="text-primary text-[11px] sm:text-[13px] font-bold tracking-tight">{t.username}</p>
              </div>
              <div className="px-5 pb-5 pt-3 sm:px-8 sm:pb-8 sm:pt-4 border-t border-gray-100">
                <p className="text-[#555] text-xs sm:text-[14px] leading-relaxed">
                  {t.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Lists Section */}
      <section className="py-6 sm:py-14 lg:py-16 max-w-7xl mx-auto px-8 sm:px-6 lg:px-12 text-left">
        <div className="space-y-12">
          <div className="space-y-4">
            <h4 className="text-[17px] font-bold text-secondary">Trending Searches</h4>
            <p className="text-[#555] text-sm flex flex-wrap gap-x-2 gap-y-3 leading-relaxed">
              {[
                "Kitchen Cabinets", "Kitchen Accessories", "Wardrobe", "Chairs", "Dining Table", "Bed", "Sofa", "Lights",
                "Chandeliers", "Lamps", "Marble", "Granite", "Tiles", "Furniture Fittings", "Door Hardware", "Locks", "Paints",
                "Finish", "Wallpaper", "Window Curtains", "Roller Blinds", "Curtain Fabrics", "Automation Systems",
                "Security Systems", "Decorative Glass", "Glass Doors", "Frameless Glass", "Wooden Doors", "Sliding Doors",
                "Panel Doors", "Wall Art", "Sculptures", "Decor Items", "POP", "Acoustic Panels", "PVC Ceiling",
                "Garden Landscaping", "Outdoor Lighting", "Water Features", "Sanitaryware", "Shower Systems",
                "Plumbing Services", "Installation Services", "Shifting", "Packing Services", "3D Modeling",
                "Design Visualization", "Vastu Planning", "Energy Balancing", "Wooden Swings", "Metal Swings", "Jhula",
                "Structural Design", "Home Electrical Services", "Wiring Work", "Leakage Repair", "PU Coating", "Air Conditioning", "HVAC Systems", "Central Air Conditioning"
              ].map((tag, i, arr) => (
                <span 
                  key={i} 
                  onClick={() => handleTagClick(tag)}
                  className="hover:text-primary cursor-pointer transition-colors whitespace-nowrap"
                >
                  {tag} {i !== arr.length - 1 && <span className="ml-2 text-gray-300">|</span>}
                </span>
              ))}
            </p>
          </div>

          <div className="space-y-4">
            <h4 className="text-[17px] font-bold text-secondary">Popular Categories</h4>
            <p className="text-[#555] text-sm flex flex-wrap gap-x-2 gap-y-3 leading-relaxed">
              {[
                "Modular Kitchen & Wardrobe", "Furniture", "Lighting", "Tile & Marble", "Plywood Supplier",
                "Hardware & Accessories", "Paint & Finishes", "Curtains & Blinds", "Home Automation", "Glass & Mirror",
                "Doors & Frames", "Art & Décor", "False Ceiling & Partitions", "Landscape & Outdoor",
                "Plumbing & Sanitary", "Movers & Installers", "3D & Visualization", "Vastu Consultant", "Swings Manufacturer",
                "Carpenter", "Civil Engineer", "Electrician", "Waterproofing Contractor", "HVAC & Air Conditioning"
              ].map((tag, i, arr) => (
                <span 
                  key={i} 
                  onClick={() => handleCategoryClick(tag)}
                  className="hover:text-primary cursor-pointer transition-colors whitespace-nowrap"
                >
                  {tag} {i !== arr.length - 1 && <span className="ml-2 text-gray-300">|</span>}
                </span>
              ))}
            </p>
          </div>

          <div className="space-y-4">
            <h4 className="text-[17px] font-bold text-secondary">Popular Cities</h4>
            <p className="text-[#555] text-sm flex flex-wrap gap-x-2 gap-y-3 leading-relaxed">
              {[
                "Ahmedabad", "Mumbai", "Bangalore", "Delhi", "Pune", "Chennai", "Hyderabad", "Kolkata"
              ].map((tag, i, arr) => (
                <span 
                  key={i} 
                  onClick={() => handleLocationClick(tag)}
                  className="hover:text-primary cursor-pointer transition-colors whitespace-nowrap"
                >
                  {tag} {i !== arr.length - 1 && <span className="ml-2 text-gray-300">|</span>}
                </span>
              ))}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
